import {
  ArrowLeftIcon,
  ArrowUUpLeftIcon,
  ArrowUUpRightIcon,
  CaretDownIcon,
  GearSixIcon,
  PauseIcon,
  PlayIcon,
  PowerIcon,
  PulseIcon,
  RocketLaunchIcon,
  UsersThreeIcon,
  WarningIcon,
} from '@phosphor-icons/react'
import { Link, useNavigate } from 'react-router-dom'
import { useConfirmation } from 'app/components/ConfirmationContext'
import { Button } from 'app/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'app/components/ui/dropdown-menu'
import { cn } from 'app/lib/utils'
import { categorySummary } from 'app/lib/workflows/deliverySummary'
import type { MessageCategory } from 'app/types/workflows'
import { InfoHint } from './inspectors/fields'

const CATEGORY_LABEL: Record<MessageCategory, string> = {
  marketing: `Marketing`,
  transactional: `Transactional`,
}

type WorkflowToolbarProps = {
  name: string
  onNameChange: (name: string) => void
  active: boolean
  /** True when the workflow is deliberately paused: patients are held at their step, no steps run,
   *  and no new patients enroll, until it is unpaused (TRK-6204). Distinct from off, which ends runs. */
  paused: boolean
  /** True while a pause/unpause request is in flight, so the control disables to prevent a double click. */
  pausing: boolean
  /** Has been live before, so off reads as Off rather than Draft. */
  wentLive: boolean
  /** Held by the engine: `error` when a step can't run (the banner says why), `paused` when held
   *  with no reason, which is how a pause by staff will read. */
  hold?: `error` | `paused`
  onActiveChange: (active: boolean) => void
  /** Pause (true) or unpause (false) a live workflow. Acts on the server at once, never via Save. */
  onTogglePause: (paused: boolean) => void
  nodeCount: number
  issueCount: number
  canUndo: boolean
  canRedo: boolean
  onUndo: () => void
  onRedo: () => void
  onSave: () => void
  onOpenRuns: () => void
  /** Opens the workflow settings panel (send window, texting category). */
  onOpenSettings: () => void
  /** Opens the Matches panel. Absent while the feature is off, which hides the button. */
  onOpenMatches?: () => void
  /** Plain-language notes on things the engine won't run as drawn yet (non-blocking) — surfaced so the
   *  builder is honest about the gap between what you see and what will actually happen. */
  wontRunYet: string[]
  /** True when the live builder has changes not yet saved to the server. */
  dirty: boolean
  /** The workspace's locations, and the one this workflow belongs to. A workflow runs for exactly
   *  one location: its number sends the texts, its timezone decides quiet hours. */
  locations: { id: string; name?: string | null }[]
  locationId?: string
  /** Opens the location chooser. */
  onOpenLocations: () => void
  /** A live workflow can't move: the API refuses a location change while it runs. */
  locationLocked?: boolean
  /** The texting category this workflow sends under, and the way into the policy panel. Absent when
   *  the graph has no texting step. `category` is the EFFECTIVE one (what the engine will do), and
   *  `pending` marks a transactional pick that only an admin's save can apply. */
  textingPolicy?: { category: MessageCategory; pending: boolean; onOpen: () => void }
}

/** Top bar above the canvas: editable name, status, undo/redo, and the save pair. A draft shows
 *  Save as draft and Set live; a live workflow shows Set changes live, and its status menu holds
 *  Pause/Unpause and Turn off. */
export function WorkflowToolbar({
  name,
  onNameChange,
  active,
  paused,
  pausing,
  wentLive,
  hold,
  onActiveChange,
  onTogglePause,
  nodeCount,
  issueCount,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  onSave,
  onOpenRuns,
  onOpenSettings,
  onOpenMatches,
  wontRunYet,
  dirty,
  locations,
  locationId,
  onOpenLocations,
  locationLocked,
  textingPolicy,
}: WorkflowToolbarProps) {
  const [confirm] = useConfirmation()
  const navigate = useNavigate()
  // Two categories, so a pick still waiting on an admin is always the other one.
  const pendingPick = textingPolicy?.category === `marketing` ? CATEGORY_LABEL.transactional : CATEGORY_LABEL.marketing

  // An empty canvas reads "Draft", not "1 to fix": there is nothing to fix before the user
  // has done anything, and opening on an error read as "I broke it" (UX audit C1).
  const label =
    hold === `error`
      ? `Error`
      : hold === `paused`
        ? `Paused`
        : nodeCount === 0
          ? `Draft`
          : issueCount > 0
            ? `${issueCount} to fix`
            : active
              ? `Live`
              : wentLive
                ? `Off`
                : `Draft`
  const tone =
    hold === `error`
      ? `font-medium text-danger-strong`
      : hold === `paused` || (issueCount > 0 && nodeCount > 0)
        ? `text-warning-strong`
        : `text-subtle`
  const mark =
    hold === `error` ? (
      <WarningIcon size={13} weight="bold" aria-hidden />
    ) : hold === `paused` ? (
      <PauseIcon size={13} weight="bold" aria-hidden />
    ) : active ? (
      <span aria-hidden className="size-1.5 rounded-full bg-success-default" />
    ) : null

  // h-[67px] matches the sidebar workspace selector so the two bottom dividers line up exactly.
  return (
    <div className="flex shrink-0 flex-wrap items-center justify-between gap-y-2 border-b border-default bg-surface px-3 py-2 md:h-[67px] md:flex-nowrap md:px-4 md:py-0">
      <div className="flex w-full min-w-0 items-center gap-1 md:w-auto">
        <Link
          to="/workflows"
          aria-label="Back to workflows"
          className="flex size-8 shrink-0 items-center justify-center rounded-md text-subtle hover:bg-surface-muted"
          // Leaving with unsaved changes used to silently discard them (UX audit 08-21, C5). The
          // confirmation promise resolves only on confirm; cancel just closes the dialog and stays.
          onClick={(event) => {
            if (!dirty) return
            event.preventDefault()
            confirm({
              title: `Discard unsaved changes?`,
              description: `This workflow has changes that haven't been saved. Leaving now throws them away.`,
              variant: `danger`,
            }).then(() => navigate(`/workflows`))
          }}
        >
          <ArrowLeftIcon size={18} weight="bold" />
        </Link>
        <div className="min-w-0">
          {/* Auto-sizing input: the invisible twin span sets the natural width of the grid cell, so
              the title never truncates until the header genuinely runs out of room (then the input
              scrolls). A hard w-72 here used to clip long QA-style names with acres of space left. */}
          <div className="-ml-1.5 grid min-w-0 max-w-full">
            <span
              aria-hidden
              className="invisible col-start-1 row-start-1 min-w-52 whitespace-pre border border-transparent px-1.5 py-0.5 text-base font-semibold"
            >
              {`${name || `Untitled workflow`}  `}
            </span>
            <input
              value={name}
              onChange={(e) => onNameChange(e.target.value)}
              placeholder="Untitled workflow"
              aria-label="Workflow name"
              className="col-start-1 row-start-1 w-full min-w-0 rounded-md border border-transparent px-1.5 py-0.5 text-base font-semibold text-strong hover:border-default focus:border-blue-500 focus:bg-surface focus:outline-none"
            />
          </div>
          <div className="flex min-w-0 items-center gap-1.5 px-1.5">
            <p className="shrink-0 text-[11px] text-subtle">
              {nodeCount} {nodeCount === 1 ? `block` : `blocks`}
            </p>
            {/* Only shown when there is a choice to make. A single-location practice has nothing to
                decide and the builder picks for them. Changing it reopens the same chooser a new
                workflow starts with, so a location that can't run a step can't be picked here either. */}
            {locations.length > 1 && (
              <>
                <span className="text-[11px] text-minimal">·</span>
                <Button
                  variant="link"
                  size="xs"
                  data-testid="workflow-location-chip"
                  disabled={locationLocked}
                  title={locationLocked ? `A live workflow keeps its location` : `Change location`}
                  onClick={onOpenLocations}
                  className="shrink-0"
                >
                  {locations.find((location) => location.id === locationId)?.name ?? locationId ?? `Choose a location`}
                </Button>
              </>
            )}
            {textingPolicy && (
              <>
                <span className="text-[11px] text-minimal">·</span>
                <Button
                  variant="ghost"
                  size="inline"
                  onClick={textingPolicy.onOpen}
                  title={
                    textingPolicy.pending
                      ? `${pendingPick} is chosen, but only a Trackable admin's save applies it. Until then these send as ${CATEGORY_LABEL[textingPolicy.category].toLowerCase()}.`
                      : categorySummary(textingPolicy.category)
                  }
                  className={cn(
                    // When room runs out, the pending note shortens; the category itself always shows.
                    `-mx-1 min-w-0 shrink rounded border border-transparent px-1 text-[11px] hover:border-default`,
                    textingPolicy.pending ? `text-warning-strong` : `text-subtle`,
                  )}
                >
                  {CATEGORY_LABEL[textingPolicy.category]}
                  {/* Name the waiting pick, or the header and the settings panel seem to disagree. */}
                  {textingPolicy.pending && <span className="truncate">({pendingPick} pending)</span>}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="flex w-full flex-wrap items-center justify-between gap-1 md:w-auto md:flex-nowrap md:justify-start md:gap-3">
        {/* A workflow that is on carries its controls in its status, so turning it off sits one
            step away from a stray click. Pause/Unpause (TRK-6204) holds patients without ending
            their runs; Turn off ends everyone's run. */}
        {active ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                id="wf-active"
                variant="white"
                size="xs"
                className={cn(`h-10 gap-1.5 md:h-8`, tone)}
                data-testid="workflow-status-menu"
              >
                {mark}
                <span data-testid="workflow-toolbar-status">{label}</span>
                <CaretDownIcon size={11} weight="bold" aria-hidden />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              <DropdownMenuItem
                disabled={pausing}
                onSelect={() => onTogglePause(!paused)}
                data-testid="workflow-toggle-pause"
                className="items-start"
              >
                {paused ? (
                  <PlayIcon size={16} weight="bold" className="mt-0.5" />
                ) : (
                  <PauseIcon size={16} weight="bold" className="mt-0.5" />
                )}
                <span className="flex flex-col">
                  {paused ? `Unpause` : `Pause`}
                  <span className="text-xs text-subtle">
                    {paused ? `Resume everyone from where they stopped` : `Hold everyone without ending their run`}
                  </span>
                </span>
              </DropdownMenuItem>
              <DropdownMenuItem
                variant="destructive"
                onSelect={() => onActiveChange(false)}
                data-testid="workflow-turn-off"
                className="items-start"
              >
                <PowerIcon size={16} weight="bold" className="mt-0.5" />
                <span className="flex flex-col">
                  Turn off
                  <span className="text-xs text-subtle">End everyone&apos;s run</span>
                </span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <span className={cn(`inline-flex items-center gap-1.5 text-xs`, tone)} data-testid="workflow-toolbar-status">
            {mark}
            {label}
          </span>
        )}

        {/* Honesty signal: steps you CAN activate, but the engine won't run exactly as drawn yet. */}
        {wontRunYet.length > 0 && (
          <span
            title={`These won't run as drawn yet (flagged for backend):\n• ${wontRunYet.join(`\n• `)}`}
            className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-700 ring-1 ring-amber-200"
          >
            <WarningIcon size={12} weight="bold" />
            {wontRunYet.length}
            <span className="hidden md:inline"> won’t run yet</span>
          </span>
        )}

        <div className="hidden h-5 w-px bg-surface-strong md:block" />

        <div className="flex items-center gap-1">
          <Button
            variant="white"
            size="icon-sm"
            onClick={onUndo}
            disabled={!canUndo}
            title="Undo (Ctrl+Z)"
            className="size-9 md:size-7"
          >
            <ArrowUUpLeftIcon size={16} weight="bold" />
          </Button>
          <Button
            variant="white"
            size="icon-sm"
            onClick={onRedo}
            disabled={!canRedo}
            title="Redo (Ctrl+Shift+Z)"
            className="size-9 md:size-7"
          >
            <ArrowUUpRightIcon size={16} weight="bold" />
          </Button>
          <Button
            variant="white"
            size="icon-sm"
            onClick={onOpenSettings}
            title="Workflow settings"
            aria-label="Workflow settings"
            className="size-9 md:size-7"
          >
            <GearSixIcon size={16} weight="bold" />
          </Button>
        </div>

        {onOpenMatches && (
          <Button
            variant="white"
            size="sm"
            onClick={onOpenMatches}
            title="See who this trigger would have fired for"
            className="size-10 md:h-8 md:w-auto"
          >
            <UsersThreeIcon size={16} weight="bold" />
            <span className="sr-only md:not-sr-only">Matches</span>
          </Button>
        )}
        <Button
          variant="white"
          size="sm"
          onClick={onOpenRuns}
          title="View runs and test on a contact"
          className="size-10 md:h-8 md:w-auto"
        >
          <PulseIcon size={16} weight="bold" />
          <span className="sr-only md:not-sr-only">Runs</span>
        </Button>
        {/* The dirty state sits beside the pair as one quiet line; the reason it matters (runs use
            the last saved version) lives behind the "i". */}
        <div className="flex items-center gap-2">
          {dirty && (
            <span className="hidden items-center text-[11px] text-subtle md:flex">
              Unsaved
              <InfoHint text="Runs and the trigger test use the last saved version until you save." />
            </span>
          )}
          {/* Intercom's pair. Set live opens the review, which saves too; the status menu holds
              Pause/Unpause and Turn off (TRK-5695/TRK-6204). A live workflow has no separate draft,
              so its save sets changes live. */}
          {active ? (
            <>
              <Button
                variant="primary"
                size="sm"
                onClick={onSave}
                disabled={!dirty}
                title={dirty ? undefined : `No changes to set live`}
                className="h-10 md:h-8"
              >
                {dirty ? `Set changes live` : `Saved`}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="white"
                size="sm"
                onClick={onSave}
                disabled={!dirty}
                title={dirty ? undefined : `No changes to save`}
                className="h-10 md:h-8"
              >
                {dirty ? `Save as draft` : `Saved`}
              </Button>
              <Button
                id="wf-active"
                data-testid="workflow-set-live"
                variant="primary"
                size="sm"
                onClick={() => onActiveChange(true)}
                disabled={issueCount > 0}
                title={issueCount > 0 && nodeCount > 0 ? `Resolve all issues before setting live` : undefined}
                className="h-10 md:h-8"
              >
                <RocketLaunchIcon size={16} weight="bold" />
                Set live
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
